import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi, setToken } from "@/lib/nexus";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name) => {
    // Registration now creates a PENDING account; no token/session is issued.
    return authApi.register(email, password, name);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.warn("[Tentacles] logout request failed; clearing local session anyway", e);
    }
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.role === "admin";
  const canWrite = !!user && user.role !== "read-only";

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser, checkAuth, isAdmin, canWrite }}>
      {children}
    </AuthCtx.Provider>
  );
}
